import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation, Trans } from 'react-i18next';

import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, FuzzyMakers } from '../../assets/svg/SVGComponents';

// import imageArduinoUnlocked from '../../../assets/images/card-arduino-color-700x420px.jpg';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';

import {
  MainContent,
  Dashboard,
  WelcomeSection,
  UserAvatar,
  ActivitiesSidebar,
  ActivitiesWrapper,
  ShimmerWrapper,
  ShimmerEffect,
  LogoFuzzyMakers,
  PerfilSection,
  HomeSection,
} from './styles';
import IRequestError from '../../dtos/IRequestError';
import ListActivities from '../../components/ListActivities';
import LogoFaceRobot from '../../assets/svg/SVGComponents/LogoFaceRobot';
import IChapterProps from '../../dtos/IChapterProps';
import ITrailsProps from '../../dtos/ITrailsProps';
import LessonList from '../../components/LessonList';
import GridHardware from '../../components/GridHardware';
import LevelDisplayComponent from '../../components/LevelDisplayComponent';
import CommunityPanel from '../../components/CommunityPanel';
import RankingPanel from '../../components/RankingPanel';
import IHomeStatsProps from '../../dtos/IHomeStatsProps';

interface IFormattedUserData {
  encodedName: string;
  encodedFirstLastName: string;
  shortName: string;
}

interface ILoadingStates {
  progress: boolean;
  homeData: boolean;
  communityData: boolean;
  rankingData: boolean;
}

const Home: React.FC = () => {
  const { user, isSessionExpired } = useAuth();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [chapterData, setChapterData] = useState<IChapterProps>();
  const [trailData, setTrailData] = useState<ITrailsProps>();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const [homeData, setHomeData] = useState<IHomeStatsProps | undefined>();
  const navigate = useNavigate();
  const [loadingState, setLoadingStates] = useState({
    progress: true,
    homeData: true,
    communityData: true,
    rankingData: true,
  });

  const updateLoadingState = useCallback(
    (key: keyof ILoadingStates, value: boolean) => {
      setLoadingStates(prev => ({ ...prev, [key]: value }));
    },
    [],
  );

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (isTabletOrMobile || isPortrait) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [isPortrait, isTabletOrMobile]);

  const fetchProgressUser = useCallback(
    async (trailSlug: string) => {
      updateLoadingState('progress', true);
      try {
        const response = await api.get(`/trails/${trailSlug}/progress`);
        setTrailData(response.data);
      } catch (err: unknown) {
        if (!(await isSessionExpired(err as IRequestError))) {
          addToast({
            type: 'error',
            expression: 'Ops.',
            description: t('toasts.errors.load_data_fail'),
          });
        }
      } finally {
        updateLoadingState('progress', false);
      }
    },
    [addToast, isSessionExpired, t, updateLoadingState],
  );

  useEffect(() => {
    if (user.current_activity?.chapters?.length > 0) {
      fetchProgressUser(user.current_activity.chapters[0].trails[0].slug);
    }
  }, [fetchProgressUser, user]);
  // não coloque sessionexpired aqui

  useEffect(() => {
    if (trailData && user.current_activity?.chapters?.length > 0) {
      const currentChapter = trailData.chapters.find(
        chapter => chapter.id === user.current_activity.chapters[0].id,
      );

      setChapterData(currentChapter);
    }
  }, [trailData, user.current_activity?.chapters]);

  const formattedUserData = useMemo<IFormattedUserData>(() => {
    const encodedName = user.name.replace(' ', '+');
    const splitName = user.name.split('');
    const fisrtName = user.name.split(' ')[0];
    const lastName = splitName[splitName.length - 1];
    const encodedFirstLastName = `${fisrtName}+${'+'}+${lastName}`;
    const shortName = fisrtName.charAt(0).toUpperCase() + fisrtName.slice(1);

    return {
      encodedName,
      shortName,
      encodedFirstLastName,
    };
  }, [user.name]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async (): Promise<void> => {
      updateLoadingState('homeData', true);
      try {
        const response = await api.get(`/user-stats`, {
          signal: controller.signal,
        });
        setHomeData(response.data);

        updateLoadingState('communityData', !response.data?.community_news);
        updateLoadingState('rankingData', !response.data?.ranking_news);
      } catch (err: unknown) {
        // Não exibir erro se for uma requisição abortada
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        if (!(await isSessionExpired(err as IRequestError))) {
          addToast({
            type: 'error',
            expression: 'Ops.',
            description: t('toasts.errors.load_data_fail'),
          });
        }
      } finally {
        updateLoadingState('homeData', false);
      }
    };

    fetchData();

    // Função de limpeza que cancela as requisições pendentes
    return () => {
      controller.abort();
    };
  }, [addToast, isSessionExpired, t, updateLoadingState]);

  const handleProjectClick = useCallback(
    (projectId: string) => {
      navigate(`/community/post/${projectId}`);
    },
    [navigate],
  );

  return (
    <MainContent>
      <Dashboard className="dashboard">
        <WelcomeSection className="welcome-section">
          <PerfilSection>
            <Link
              data-testid="img-profile"
              to="/profile"
              className="perfil-access"
            >
              <UserAvatar
                $avatarSrc={
                  user.avatar_url ||
                  'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                }
              />
            </Link>

            <span>
              <Link to="/profile" className="perfil-access">
                <Trans
                  i18nKey="spans.home_greetings"
                  values={{ user_name: formattedUserData.shortName }}
                />
              </Link>
              <p>{t('p.check_week_summary')}</p>
            </span>
          </PerfilSection>
          <LevelDisplayComponent
            displayMode="compact"
            current_points={user.total_points}
          />
        </WelcomeSection>
        {!isMobile && (
          <HomeSection>
            <CommunityPanel
              project_user_highlighted={
                homeData?.community_news.project_user_highlighted
              }
              feed_posts={homeData?.community_news.feed_posts}
              isLoading={loadingState.communityData}
              onProjectClick={handleProjectClick}
            />
            <RankingPanel
              top_users={homeData?.ranking_news.top_users}
              isLoading={loadingState.rankingData}
            />
            <GridHardware />
          </HomeSection>
        )}
      </Dashboard>

      <ActivitiesSidebar className="activities-sidebar">
        <LogoFuzzyMakers>
          <LogoFaceRobot fill="#fff" width="2.84rem" />
          <FuzzyMakers fill="none" width="16.58rem" />
        </LogoFuzzyMakers>

        <header>
          <Link to="/projects/progress">
            <h3>{t('h3.access_playlists')}</h3>
            <ChevronRightIcon height="1.12rem" stroke="#fff" strokeWidth={2} />
          </Link>

          <h4>
            <Trans i18nKey="h4.continue_where_you_left_of" />
          </h4>
        </header>
        <ActivitiesWrapper>
          {loadingState.progress && (
            <ShimmerWrapper $isMobile={isMobile}>
              <ShimmerEffect className="shimmer-medium-title" />
              <ShimmerEffect className="shimmer-activity" />
              {!isMobile && (
                <>
                  <ShimmerEffect className="shimmer-activity" />
                  <ShimmerEffect className="shimmer-activity" />
                </>
              )}
            </ShimmerWrapper>
          )}
          {!loadingState.progress && chapterData && (
            <>
              <h2>
                <strong>
                  <Trans
                    i18nKey="h2.chapter_number"
                    values={{ number: chapterData.order }}
                  />
                </strong>
                <Trans i18nKey={`chapters.${chapterData.slug}.title`} />
              </h2>
              {chapterData.type === 'activity' && (
                <ListActivities
                  trail_slug={
                    user.current_activity?.chapters?.[0]?.trails?.[0]?.slug
                  }
                  chapter_id={chapterData.id}
                  activities={
                    isMobile
                      ? chapterData.activities.slice(0, 1)
                      : chapterData.activities
                  }
                />
              )}
              {chapterData.type === 'lesson' && (
                <LessonList activities={chapterData.lessons} />
              )}
            </>
          )}
        </ActivitiesWrapper>
      </ActivitiesSidebar>
      {isMobile && (
        <>
          <CommunityPanel
            project_user_highlighted={
              homeData?.community_news.project_user_highlighted
            }
            feed_posts={homeData?.community_news.feed_posts}
            isLoading={loadingState.communityData}
            onProjectClick={handleProjectClick}
          />
          <RankingPanel
            top_users={homeData?.ranking_news.top_users}
            isLoading={loadingState.rankingData}
          />
          <GridHardware />
        </>
      )}
    </MainContent>
  );
};

export default Home;
